import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, createVNode as _createVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-04b500f7"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "page-view-stats__period" }
const _hoisted_2 = { class: "page-view-stats" }
const _hoisted_3 = { class: "page-view-stats__total" }
const _hoisted_4 = { class: "page-view-stats__total-value" }

import {computed, onMounted, ref} from "vue";
import {useI18n} from "vue3-i18n";
import Card from "@/components/layout/Card.vue";
import PageViewBarChart from "@/components/charts/PageViewBarChart.vue";
import {useOrganizationService} from "@/inversify.config";
import {ICorporatePageViewStats} from "@/types/corporatePageViewStats";

// Beyond this span the monthly bars become unreadable, so we aggregate by year instead.
const MAX_MONTHLY_SPAN = 24;

// eslint-disable-next-line no-undef

export default /*#__PURE__*/_defineComponent({
  __name: 'CorporatePageViewStatsCard',
  props: {
    organizationId: {}
  },
  setup(__props: any) {

const props = __props;

const {t} = useI18n();
const organizationService = useOrganizationService();

const stats = ref<ICorporatePageViewStats>();
const periodMode = ref<"last12" | "all">("last12");

onMounted(async () => {
  try {
    // Fetch the full history once (since 2000); the period toggle slices it client-side.
    stats.value = await organizationService.getCorporatePageViewStats(props.organizationId, "2000-01-01");
  } catch {
    stats.value = {total: 0, daily: []};
  }
});

function monthKey(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
}

// Zero-filled buckets from the start of the selected period to now, folded with the API daily counts.
// "last12" is always the last 12 months (monthly). "all" starts at the first recorded view and switches
// to yearly buckets once the span exceeds MAX_MONTHLY_SPAN, so even 10+ years stays readable.
const buckets = computed(() => {
  const daily = stats.value?.daily ?? [];
  const now = new Date();

  let start = new Date(now.getFullYear(), now.getMonth() - 11, 1);
  if (periodMode.value === "all" && daily.length > 0) {
    const earliest = [...daily.map(d => d.date)].sort()[0];
    if (earliest) {
      const earliestStart = new Date(Number(earliest.substring(0, 4)), Number(earliest.substring(5, 7)) - 1, 1);
      if (earliestStart < start)
        start = earliestStart;
    }
  }

  const monthsSpan = (now.getFullYear() - start.getFullYear()) * 12 + (now.getMonth() - start.getMonth()) + 1;
  const byYear = monthsSpan > MAX_MONTHLY_SPAN;
  const counts = new Map<string, number>();

  if (byYear) {
    for (let year = start.getFullYear(); year <= now.getFullYear(); year++)
      counts.set(String(year), 0);
    for (const day of daily) {
      const key = day.date.substring(0, 4);
      if (counts.has(key))
        counts.set(key, (counts.get(key) ?? 0) + day.count);
    }
  } else {
    for (let cursor = start; cursor <= now; cursor = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))
      counts.set(monthKey(cursor), 0);
    for (const day of daily) {
      const key = day.date.substring(0, 7);
      if (counts.has(key))
        counts.set(key, (counts.get(key) ?? 0) + day.count);
    }
  }

  return {byYear, entries: [...counts.entries()]};
});

const chartLabels = computed(() => buckets.value.entries.map(entry => entry[0]));
const chartValues = computed(() => buckets.value.entries.map(entry => entry[1]));
const displayedTotal = computed(() => buckets.value.entries.reduce((sum, entry) => sum + entry[1], 0));
const chartLabel = computed(() =>
    buckets.value.byYear ? t("organization.pageViews.yearlyChart") : t("organization.pageViews.monthlyChart"));
const periodLabel = computed(() =>
    periodMode.value === "all" ? t("organization.pageViews.periodAll") : t("organization.pageViews.period"));

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(Card, {
    title: _unref(t)('organization.pageViews.title')
  }, {
    "on-title-right": _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createElementVNode("button", {
          type: "button",
          class: _normalizeClass(["page-view-stats__period-btn", { 'page-view-stats__period-btn--active': periodMode.value === 'last12' }]),
          onClick: _cache[0] || (_cache[0] = ($event: any) => (periodMode.value = 'last12'))
        }, _toDisplayString(_unref(t)('organization.pageViews.period')), 3),
        _createElementVNode("button", {
          type: "button",
          class: _normalizeClass(["page-view-stats__period-btn", { 'page-view-stats__period-btn--active': periodMode.value === 'all' }]),
          onClick: _cache[1] || (_cache[1] = ($event: any) => (periodMode.value = 'all'))
        }, _toDisplayString(_unref(t)('organization.pageViews.periodAll')), 3)
      ])
    ]),
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_2, [
        _createElementVNode("p", _hoisted_3, [
          _createElementVNode("span", _hoisted_4, _toDisplayString(displayedTotal.value), 1),
          _createTextVNode(" " + _toDisplayString(_unref(t)('organization.pageViews.total')) + " (" + _toDisplayString(periodLabel.value) + ") ", 1)
        ]),
        _createVNode(PageViewBarChart, {
          labels: chartLabels.value,
          values: chartValues.value,
          label: chartLabel.value
        }, null, 8, ["labels", "values", "label"])
      ])
    ]),
    _: 1
  }, 8, ["title"]))
}
}

})